import {inject, Injectable} from '@angular/core';
import {ScriptService} from '@core/services/script.service';

/** The part of Trustpilot's global the storefront uses. */
export interface TrustpilotGlobal {
  loadFromElement(element: Element, forceReload?: boolean): void;
}

/**
 * Trustpilot's bootstrap script, loaded once for every TrustBox on the page.
 *
 * When it first loads, the script initialises every `.trustpilot-widget` already
 * in the document — including TrustBoxes placed in Storyblok content, which have
 * no component of their own to load it. A placeholder rendered after that has to
 * be handed to `loadFromElement`.
 *
 * Browser only: callers check the platform first.
 */
@Injectable({providedIn: 'root'})
export class TrustpilotScriptService {
  private readonly scriptService = inject(ScriptService);
  private loaded?: Promise<TrustpilotGlobal | null>;

  /**
   * Trustpilot's global once the script has loaded, injecting it on the first
   * call. Every caller waits for the same load, so one that asks while another's
   * script tag is still loading is not left with nothing. Null when the script
   * cannot load — blocked by the browser, or Trustpilot down — which leaves the
   * placeholder's plain link in place.
   */
  ready(): Promise<TrustpilotGlobal | null> {
    this.loaded ??= this.scriptService
      .addScript({
        type: 'text/javascript',
        id: 'trustpilot-widget-script',
        src: 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
      }, false, 'head')
      .then(() => (window as {Trustpilot?: TrustpilotGlobal}).Trustpilot ?? null)
      .catch(() => null);
    return this.loaded;
  }
}
